import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs
} from "firebase/firestore";
import { useParams, Link } from "react-router-dom";
import {
  FaBookmark,
  FaExternalLinkAlt,
  FaCopy,
  FaEyeSlash,
  FaFlag,
  FaTimes,
  FaEllipsisH,
  FaExpand,
  FaSearch,
  FaHeart,
  FaShare
} from "react-icons/fa";
import "./FeedDetails.css";

export default function FeedDetails() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  // load main post
  useEffect(() => {
    (async () => {
      const pd = await getDoc(doc(db, "posts", postId));
      if (!pd.exists()) return;
      const data = pd.data();
      const mediaList = Array.isArray(data.media) && data.media.length
        ? data.media
        : Array.isArray(data.compressedMedia)
          ? data.compressedMedia
          : [];
      const parsed = mediaList.map(r =>
        typeof r === "string" ? JSON.parse(r) : r
      ).filter(Boolean);
      setPost({ id: pd.id, ...data, _media: parsed });
    })();
  }, [postId]);

  // suggestions
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "posts"));
      const all = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => !d.isDeleted && d.id !== postId);
      // shuffle
      for (let i = all.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [all[i], all[j]] = [all[j], all[i]];
      }
      setSuggestions(all.slice(0, 4));
    })();
  }, [postId]);

  if (!post) return <div className="details-loading">Loading post…</div>;

  const mainEntry = post._media[0] || {};

  //share posts
  const handleSharePost = (postId, postTitle = "Check out this post!") => {
  const postUrl = `${window.location.origin}/post/${postId}`;

  if (navigator.share) {
    navigator.share({
      title: postTitle,
      text: `${postTitle} on our platform.`,
      url: postUrl,
    }).catch(err => {
      console.error('Error sharing:', err);
      copyToClipboard(postUrl);
      alert("Link copied to clipboard!");
    });
  } else {
    copyToClipboard(postUrl);
    alert("Link copied to clipboard!");
  }
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    alert('Profile link copied to clipboard!');
  }).catch(err => {
    console.error('Failed to copy:', err);
    // Fallback if clipboard API fails
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    alert('Profile link copied to clipboard!');
  });
};

  return (
    <div className="details-wrapper">
      {/* Main column */}
      <div className="details-main">
        <div className="details-header">
          <div className="user-info">
            <a
              href={`/profile/${post.postedById}`}
              // target="_blank"
              // rel="noopener noreferrer"
              className="user-link"
            >
              <img
                src={post.postedByProfileImg}
                alt={post.postedByUsername}
                className="details-avatar"
              />
              <span className="details-username">
                {post.postedByUsername}
              </span>
            </a>
          </div>
          <div className="post-options">
            <button
              className="options-trigger"
              onClick={() => setMenuOpen(o => !o)}
            >
              <FaEllipsisH />
            </button>
            {menuOpen && (
              <div className="options-menu">
                <button className="option-item">
                  <FaBookmark className="option-icon" /> Save
                </button>
                <button className="option-item">
                  <FaExternalLinkAlt className="option-icon" /> Go to post
                </button>
                <button
                  className="option-item"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                  }}
                >
                  <FaCopy className="option-icon" /> Copy link
                </button>
                <button className="option-item">
                  <FaEyeSlash className="option-icon" /> Hide
                </button>
                <button className="option-item">
                  <FaFlag className="option-icon" /> Report
                </button>
                <button
                  className="option-item cancel"
                  onClick={() => setMenuOpen(false)}
                >
                  <FaTimes className="option-icon" /> Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="details-media">
          {mainEntry.type === "image" && (
            <img src={mainEntry.url} alt="" className="main-media"/>
          )}
          {mainEntry.type === "video" && (
            <video controls src={mainEntry.url} className="main-media"/>
          )}

          {/* overlay buttons */}
          <div className="media-overlay">
            <button
              className="overlay-btn"
              title="Explore"
            >
              <FaSearch className="overlay-btn-icon" />
            </button>
            <button
              className="overlay-btn"
              title="View larger"
              onClick={() => setFullScreen(true)}
            >
              <FaExpand  className="overlay-btn-icon"/>
            </button>
          </div>
        </div>

        {/* full screen */}
        {fullScreen && (
          <div className="fullscreen-overlay">
            <button
              className="close-full"
              onClick={() => setFullScreen(false)}
            >
              <FaTimes />
            </button>
            {mainEntry.type === "image" && (
              <img src={mainEntry.url} alt="" className="fullscreen-media"/>
            )}
            {mainEntry.type === "video" && (
              <video controls src={mainEntry.url} className="fullscreen-media"/>
            )}
          </div>
        )}

        {/* interactions */}
        <div className="details-interactions">
          <button className="interaction-btn">
            <FaHeart /> Like
          </button>
          <button className="interaction-btn" onClick={handleSharePost}>
            <FaShare /> Share
          </button>
        </div>

        {post.caption && (
          <p className="details-caption">{post.caption}</p>
        )}
      </div>

      {/* Suggestions */}
      <aside className="details-suggestions">
        <h3>You might also like</h3>
        <div className="suggestions-list">
          {suggestions.map(p => {
            const ml = Array.isArray(p.media) && p.media.length
              ? p.media
              : Array.isArray(p.compressedMedia)
                ? p.compressedMedia
                : [];
            const e = ml
              .map(r => typeof r==="string" ? JSON.parse(r) : r)
              .filter(Boolean)[0] || {};
            return (
              <Link
                key={p.id}
                to={`/feed-details/${p.id}`}
                // target="_blank"
                className="suggestion-card"
              >
                {e.type === "image" && (
                  <img src={e.url} alt="" className="suggestion-media" />
                )}
                {e.type === "video" && (
                  <video src={e.url} className="suggestion-media" />
                )}
                <div className="suggestion-username">
                  {p.postedByUsername}
                </div>
              </Link>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
